import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
} from "@assistant-ui/react";
import { ChatSelectionProvider } from "./ChatSelectionContext";
import ChatConsentModal from "./ChatConsentModal";
import ChatLauncher from "./ChatLauncher";
import ChatPanel from "./ChatPanel";
import ChatPlainPostPanel from "./ChatPlainPostPanel";
import useTheme from "../../common/hooks/useTheme";
import { getChatResponseCssVariables } from "./designSystem/chatResponseSurface";
import {
  resolveChatDevControlsOverride,
  resolveChatScenarioOverride,
} from "./chatMode";
import { getScenarioCommand } from "./mock/mockScenarios";
import {
  createMockChatModelAdapter,
  toAssistantAppendMessage,
} from "./mock/mockEngine";
import {
  BiomarkerSuggestionItem,
  ChatCommandRouterServer,
  ChatProviderMode,
  MockScenarioId,
} from "./types";
import { useAppStore, useConfig } from "../../providers/AppStore";
import { useAuth } from "../../providers/AuthProvider";
import { LocalStorageVariables } from "../../common/entities/type";
import {
  getCustomerSession,
  updateCustomerToken,
} from "../../common/network/customerSession";
import ENDPOINTS from "../../common/network/Endpoints";
import classes from "./Chat.module.css";

type Props = {
  surfaceId: string;
  providerMode: ChatProviderMode;
};

type ChatPanelMode = "compact" | "expanded";
type PrescriptionPanelMode = "expanded" | "collapsed";
const CHAT_TERMS_ACCEPTED_KEY = "pnoe.chat.termsAccepted";
const CHAT_COMMAND_ROUTER_SERVER_KEY = "pnoe.chat.commandRouterServer";

const ChatRoot = ({
  surfaceId,
  providerMode,
}: Props) => {
  const theme = useTheme();
  const config = useConfig();
  const location = useLocation();
  const { ASSelectedClient, ASSetSelectedClient } = useAppStore();
  const { AuthUser, AuthIsSimpleUser, AuthSetUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isConsentOpen, setIsConsentOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<ChatPanelMode>("compact");
  const [commandRouterServer, setCommandRouterServer] =
    useState<ChatCommandRouterServer>(() => {
      if (typeof window === "undefined") {
        return "prod";
      }

      const storedValue = window.localStorage.getItem(
        CHAT_COMMAND_ROUTER_SERVER_KEY
      );
      return storedValue === "dev" ? "dev" : "prod";
    });
  const [composerHistoryKey, setComposerHistoryKey] = useState(0);
  const [scrollToBottomRequestId, setScrollToBottomRequestId] = useState(0);
  const [plainPostMarkdown, setPlainPostMarkdown] = useState<string | null>(
    null
  );
  const [prescriptionPanelMode, setPrescriptionPanelMode] =
    useState<PrescriptionPanelMode>("expanded");
  const [isPrescriptionIgnored, setIsPrescriptionIgnored] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.localStorage.getItem(CHAT_TERMS_ACCEPTED_KEY) === "true";
  });
  const [selectedMetricPromptByMessageId, setSelectedMetricPromptByMessageId] =
    useState<Record<string, string>>({});
  const [
    selectedBiomarkerPromptByMessageId,
    setSelectedBiomarkerPromptByMessageId,
  ] = useState<Record<string, string>>({});
  const lastSurfaceRef = useRef(surfaceId);
  const bootScenarioRef = useRef<string | null>(null);
  const pendingScenarioRef = useRef<MockScenarioId | null>(null);
  const selectedClientId = useMemo(() => {
    if (surfaceId !== "platform") {
      return null;
    }

    return new URLSearchParams(location.search).get("id");
  }, [location.search, surfaceId]);
  const selectedCustomerIdForCommandRouter = useMemo(() => {
    if (selectedClientId) {
      return selectedClientId;
    }

    if (ASSelectedClient?.id) {
      return String(ASSelectedClient.id);
    }

    return null;
  }, [ASSelectedClient, selectedClientId]);
  const selectedCustomerIdRef = useRef<string | null>(
    selectedCustomerIdForCommandRouter
  );
  const authorizationTokenForCommandRouter = useMemo(() => {
    const selectedCustomerToken =
      typeof ASSelectedClient?.token === "string"
        ? ASSelectedClient.token.trim()
        : "";
    if (selectedCustomerToken) {
      return selectedCustomerToken;
    }

    if (typeof window !== "undefined") {
      const storedCustomer = getCustomerSession(
        selectedCustomerIdForCommandRouter ?? undefined
      );
      const storedCustomerToken =
        typeof storedCustomer?.token === "string"
          ? storedCustomer.token.trim()
          : "";
      if (storedCustomerToken) {
        return storedCustomerToken;
      }
    }

    if (!AuthIsSimpleUser) {
      return null;
    }

    const authUserToken =
      typeof AuthUser?.token === "string" ? AuthUser.token.trim() : "";
    return authUserToken || null;
  }, [ASSelectedClient, AuthIsSimpleUser, AuthUser, selectedCustomerIdForCommandRouter]);
  const authorizationTokenRef = useRef<string | null>(
    authorizationTokenForCommandRouter
  );
  const isPrescriptionIgnoredRef = useRef(isPrescriptionIgnored);
  const commandRouterServerRef =
    useRef<ChatCommandRouterServer>(commandRouterServer);
  const commandRouterSessionIdRef = useRef("");

  useEffect(() => {
    selectedCustomerIdRef.current = selectedCustomerIdForCommandRouter;
  }, [selectedCustomerIdForCommandRouter]);

  useEffect(() => {
    authorizationTokenRef.current = authorizationTokenForCommandRouter;
  }, [authorizationTokenForCommandRouter]);

  useEffect(() => {
    isPrescriptionIgnoredRef.current = isPrescriptionIgnored;
  }, [isPrescriptionIgnored]);

  useEffect(() => {
    commandRouterServerRef.current = commandRouterServer;
    window.localStorage.setItem(
      CHAT_COMMAND_ROUTER_SERVER_KEY,
      commandRouterServer
    );
  }, [commandRouterServer]);

  const setCommandRouterSessionId = useCallback((sessionId: string) => {
    commandRouterSessionIdRef.current = sessionId;
  }, []);

  const resetCommandRouterSessionId = useCallback(() => {
    commandRouterSessionIdRef.current = "";
  }, []);

  const handlePrescriptionCommandResolved = useCallback((markdownText: string) => {
    setPlainPostMarkdown(markdownText);
    setPrescriptionPanelMode("expanded");
  }, []);

  const refreshAuthorizationToken = useCallback(
    async (currentToken: string | null): Promise<string | null> => {
      const getStoredUser = () => {
        try {
          const raw = window.localStorage.getItem(LocalStorageVariables.USER);
          if (!raw) {
            return null;
          }
          return JSON.parse(raw) as Record<string, unknown>;
        } catch (error) {
          return null;
        }
      };

      // Affiliates chat on behalf of the selected client, so the token must
      // refresh with the client's own credentials from the tab's customer
      // session. Simple users refresh their own user record.
      const customerSession = !AuthIsSimpleUser
        ? getCustomerSession(selectedCustomerIdRef.current ?? undefined)
        : null;

      const authUserRecord =
        AuthUser && typeof AuthUser === "object"
          ? (AuthUser as unknown as Record<string, unknown>)
          : null;
      const storedUser = getStoredUser();
      const refreshSource = customerSession
        ? (customerSession as unknown as Record<string, unknown>)
        : authUserRecord || storedUser;
      if (!refreshSource) {
        return null;
      }

      const identity =
        typeof refreshSource.identity === "string"
          ? refreshSource.identity.trim()
          : "";
      const refreshToken =
        typeof refreshSource.refresh_token === "string"
          ? refreshSource.refresh_token.trim()
          : "";
      if (!identity || !refreshToken) {
        return null;
      }

      try {
        const refreshResponse = await axios.post(
          `${config.ID_BASE_URL}${ENDPOINTS.ID.REFRESH_TOKEN_URL}`,
          {
            id: identity,
            refresh_token: refreshToken,
          }
        );
        const refreshedToken =
          typeof refreshResponse.data?.id_token === "string"
            ? refreshResponse.data.id_token.trim()
            : "";
        if (!refreshedToken) {
          return null;
        }
        const normalizedCurrentToken = currentToken?.trim() || "";
        if (normalizedCurrentToken && refreshedToken === normalizedCurrentToken) {
          authorizationTokenRef.current = refreshedToken;
          return refreshedToken;
        }

        if (customerSession) {
          // Refreshed on behalf of the selected client — update the tab's
          // customer session and its React mirror, never the affiliate record.
          updateCustomerToken(refreshedToken);
          if (ASSelectedClient) {
            await ASSetSelectedClient({
              ...ASSelectedClient,
              token: refreshedToken,
            });
          }
        } else {
          const nextUserRecord = {
            ...(storedUser || authUserRecord || {}),
            token: refreshedToken,
          };
          window.localStorage.setItem(
            LocalStorageVariables.USER,
            JSON.stringify(nextUserRecord)
          );
          await AuthSetUser(nextUserRecord);

          // Role-1 consultation mirrors the user into ASSelectedClient —
          // keep that mirror in sync.
          if (AuthIsSimpleUser && ASSelectedClient) {
            await ASSetSelectedClient({
              ...ASSelectedClient,
              token: refreshedToken,
            });
          }
        }

        authorizationTokenRef.current = refreshedToken;
        return refreshedToken;
      } catch (error) {
        return null;
      }
    },
    [
      ASSelectedClient,
      ASSetSelectedClient,
      AuthSetUser,
      AuthUser,
      AuthIsSimpleUser,
      config.ID_BASE_URL,
    ]
  );

  const chatModelAdapter = useMemo(() => {
    return createMockChatModelAdapter({
      getStreamSpeed: () => "normal",
      getSelectedCustomerId: () => selectedCustomerIdRef.current,
      getAuthorizationToken: () => authorizationTokenRef.current,
      refreshAuthorizationToken,
      getCommandRouterServer: () => commandRouterServerRef.current,
      getCommandRouterSessionId: () => commandRouterSessionIdRef.current,
      setCommandRouterSessionId,
      onPrescriptionCommandResolved: handlePrescriptionCommandResolved,
      shouldIgnorePrescriptionCommands: () => isPrescriptionIgnoredRef.current,
    });
  }, [
    handlePrescriptionCommandResolved,
    refreshAuthorizationToken,
    setCommandRouterSessionId,
  ]);

  const runtime = useLocalRuntime(chatModelAdapter);

  const isChatDevEnabled = resolveChatDevControlsOverride(location.search);
  const showDevControls = providerMode === "mock" && isChatDevEnabled;
  const showEnvironmentSelector = providerMode === "mock" && isChatDevEnabled;

  const scenarioOverride = useMemo(() => {
    return resolveChatScenarioOverride(location.search);
  }, [location.search]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    if (hasAcceptedTerms) {
      setIsOpen(true);
      return;
    }

    setIsConsentOpen(true);
  }, [hasAcceptedTerms, isOpen]);

  const handleAcceptTerms = useCallback(() => {
    window.localStorage.setItem(CHAT_TERMS_ACCEPTED_KEY, "true");
    setHasAcceptedTerms(true);
    setIsConsentOpen(false);
    if (pendingScenarioRef.current) {
      runtime.thread.reset();
      resetCommandRouterSessionId();
      setComposerHistoryKey((currentKey) => currentKey + 1);
      setSelectedMetricPromptByMessageId({});
      setSelectedBiomarkerPromptByMessageId({});
      runtime.thread.append(getScenarioCommand(pendingScenarioRef.current));
      pendingScenarioRef.current = null;
    }
    setIsOpen(true);
  }, [resetCommandRouterSessionId, runtime]);

  const handleCloseConsent = useCallback(() => {
    pendingScenarioRef.current = null;
    setIsConsentOpen(false);
  }, []);

  const handleToggleExpanded = useCallback(() => {
    setPanelMode((currentMode) =>
      currentMode === "expanded" ? "compact" : "expanded"
    );
  }, []);

  const setSelectedMetricPrompt = useCallback(
    (messageId: string, prompt: string) => {
      setSelectedMetricPromptByMessageId((currentSelections) => ({
        ...currentSelections,
        [messageId]: prompt,
      }));
    },
    []
  );

  const resetSelections = useCallback(() => {
    setSelectedMetricPromptByMessageId({});
    setSelectedBiomarkerPromptByMessageId({});
  }, []);

  const requestScrollToBottom = useCallback(() => {
    setScrollToBottomRequestId((currentId) => currentId + 1);
  }, []);

  const handlePostPlainPayload = useCallback(() => {
    // Reserved for follow-up server action; current UX only requires button click affordance.
  }, []);

  const handleDismissPrescriptionPanel = useCallback(() => {
    setPlainPostMarkdown(null);
  }, []);

  const handleTogglePrescriptionPanelCollapsed = useCallback(() => {
    setPrescriptionPanelMode((currentMode) =>
      currentMode === "expanded" ? "collapsed" : "expanded"
    );
  }, []);

  const setSelectedBiomarkerPrompt = useCallback(
    (messageId: string, prompt: string) => {
      setSelectedBiomarkerPromptByMessageId((currentSelections) => ({
        ...currentSelections,
        [messageId]: prompt,
      }));
    },
    []
  );

  const selectBiomarkerSuggestion = useCallback(
    (messageId: string, suggestion: BiomarkerSuggestionItem) => {
      setSelectedBiomarkerPromptByMessageId((currentSelections) => ({
        ...currentSelections,
        [messageId]: suggestion.prompt,
      }));
      requestScrollToBottom();

      const biomarkerResponse = {
        scenarioId: "biomarker_detail" as const,
        parts: [
          {
            type: "biomarker_detail" as const,
            data: suggestion.biomarkerDetail,
          },
        ],
      };
      runtime.thread.append(toAssistantAppendMessage(biomarkerResponse));
    },
    [requestScrollToBottom, runtime]
  );

  useEffect(() => {
    if (lastSurfaceRef.current === surfaceId) {
      return;
    }

    lastSurfaceRef.current = surfaceId;
    runtime.thread.cancelRun();
    runtime.thread.reset();
    resetCommandRouterSessionId();
    setComposerHistoryKey((currentKey) => currentKey + 1);
    setPlainPostMarkdown(null);
    setPrescriptionPanelMode("expanded");
    setIsPrescriptionIgnored(false);
    setSelectedMetricPromptByMessageId({});
    setSelectedBiomarkerPromptByMessageId({});
    setPanelMode("compact");
    setIsOpen(false);
    setIsConsentOpen(false);
    bootScenarioRef.current = null;
    pendingScenarioRef.current = null;
  }, [resetCommandRouterSessionId, runtime, surfaceId]);

  useEffect(() => {
    if (!scenarioOverride) {
      return;
    }

    const bootKey = `${surfaceId}:${scenarioOverride}`;
    if (bootScenarioRef.current === bootKey) {
      return;
    }

    if (!hasAcceptedTerms) {
      pendingScenarioRef.current = scenarioOverride;
      setIsConsentOpen(true);
      bootScenarioRef.current = bootKey;
      return;
    }

    runtime.thread.reset();
    resetCommandRouterSessionId();
    setComposerHistoryKey((currentKey) => currentKey + 1);
    setSelectedMetricPromptByMessageId({});
    setSelectedBiomarkerPromptByMessageId({});
    runtime.thread.append(getScenarioCommand(scenarioOverride));
    setIsOpen(true);
    bootScenarioRef.current = bootKey;
  }, [hasAcceptedTerms, resetCommandRouterSessionId, runtime, scenarioOverride, surfaceId]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ChatSelectionProvider
        selectedMetricPromptByMessageId={selectedMetricPromptByMessageId}
        selectedBiomarkerPromptByMessageId={
          selectedBiomarkerPromptByMessageId
        }
        scrollToBottomRequestId={scrollToBottomRequestId}
        setSelectedMetricPrompt={setSelectedMetricPrompt}
        setSelectedBiomarkerPrompt={setSelectedBiomarkerPrompt}
        selectBiomarkerSuggestion={selectBiomarkerSuggestion}
        requestScrollToBottom={requestScrollToBottom}
        resetSelections={resetSelections}
      >
        <div
          className={classes.chatRoot}
          style={getChatResponseCssVariables(theme)}
        >
          <ChatConsentModal
            isOpen={isConsentOpen}
            onAccept={handleAcceptTerms}
            onClose={handleCloseConsent}
          />
          <div className={classes.chatPanelsDock}>
            <ChatPlainPostPanel
              isVisible={isOpen && plainPostMarkdown !== null}
              isCollapsed={prescriptionPanelMode === "collapsed"}
              markdownText={plainPostMarkdown}
              onPost={handlePostPlainPayload}
              onToggleCollapsed={handleTogglePrescriptionPanelCollapsed}
              onDismiss={handleDismissPrescriptionPanel}
            />
            <ChatPanel
              isOpen={isOpen}
              isExpanded={panelMode === "expanded"}
              composerHistoryKey={composerHistoryKey}
              surfaceId={surfaceId}
              showDevControls={showDevControls}
              showEnvironmentSelector={showEnvironmentSelector}
              commandRouterServer={commandRouterServer}
              onCommandRouterServerChange={setCommandRouterServer}
              onToggleExpanded={handleToggleExpanded}
            />
          </div>
          <ChatLauncher
            isOpen={isOpen}
            onToggle={handleToggle}
          />
        </div>
      </ChatSelectionProvider>
    </AssistantRuntimeProvider>
  );
};

export default ChatRoot;
